import { Button, HideInProduction } from '@/components'
import { FAIcon, Button as OldButton, SVGIcon } from '@/components_deprecated'
import type { SalaryModuleType } from '@/modules/companies'
import { SalaryWorkTimesTables } from '@/modules/companies/CompanySettings/components/SalarySettings/SalaryWorkTimesTables'
import { SelectSalaryModule } from '@/modules/companies/CompanySettings/components/SalarySettings/SelectSalaryModule'
import { useSalaryModuleTranslations } from '@/modules/companies/CompanySettings/hooks'
import { FixedHourlyAddons } from '@/modules/salary/FixedHourlyAddons'
import { FixedSalaryAddons } from '@/modules/salary/FixedSalaryAddons'
import { FixedSalaryDayAddons } from '@/modules/salary/FixedSalaryDayAddons'
import { ZIndexRange } from '@/types/style'
import { STAGE } from '@/utils'
import { notBlankValidation } from '@/utils/forms'
import {
  useAdmin,
  useBreadcrumbs,
  useCompany,
  useConfirm,
  useOnErrorAuto,
  useParam,
  usePermissions,
  useUser,
} from '@/utils/hooks'
import { emailIsValid } from '@/utils/parsing'
import { PERMISSIONS } from '@/utils/permissions'
import { useMutation, useQuery } from '@apollo/client'
import { Icon, Loader, useToast } from '@ur-solutions/react-components'
import {
  useClickOutside,
  useForm,
  useTranslate,
} from '@ur-solutions/react-hooks'
import { isPossibleNumber } from 'libphonenumber-js'
import difference from 'lodash/difference'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { isMobile, isMobileOnly } from 'react-device-detect'
import { useLocation, useNavigate } from 'react-router'
import styled, { css } from 'styled-components'
import type { UserNode, UserTypeNode } from '..'
import { DriverCard } from '../DriverCard'
import { PATCH_USER_MUTATION } from '../mutations'
import { PROFILE_QUERY } from '../queries'
import type { ProfileEditForm } from '../types'
import { UserDocuments } from '../UserDocuments/UserDocuments'
import { UserEquipmentTable } from '../UserEquipmentTable'
import { AddDriverCard } from './AddDriverCard'
import { ChildcareCard } from './childcare-card'
import { OldProfileCard } from './components'
import { ContactPerson } from './ContactPerson'
import { MainInfo } from './MainInfo'

const ExpiredCards = styled.div`
  grid-area: expired-cards;

  display: flex;
  flex-direction: column;
  gap: 1rem;
`

interface WrapperProps {
  $editing: boolean
}
const Wrapper = styled.div<WrapperProps>`
  ${props => props.theme.layout.default};

  display: grid;
  grid-template-columns: 2fr 2fr;

  ${() =>
    STAGE === 'production'
      ? css`
          grid-template-areas:
            'header           header'
            'main             main'
            'contact-and-card docs'
            'expired-cards    expired-cards'
            'salary-header    salary-header'
            'salary           salary';
        `
      : css`
          grid-template-areas:
            'header           header'
            'main             main'
            'contact-and-card docs'
            'expired-cards    expired-cards'
            'salary-header    childcare'
            'salary           childcare';
        `};

  gap: 1.5rem;

  header {
    grid-area: header;

    display: flex;
    justify-content: space-between;
    align-items: center;

    h2 {
      margin: 0;
      font-weight: 600;
      span {
        color: ${props => props.theme.colors.text.dark};
      }
    }
    div {
      display: flex;

      button + button {
        margin-left: 1rem;
      }
    }
  }

  ${props => props.theme.media.custom({ max: '1620px' })} {
    display: flex;
    flex-direction: column;
    gap: 0;

    & > * + * {
      margin-top: 1.5rem;
    }

    ${props =>
      props.$editing &&
      css`
        & > header {
          justify-content: flex-end;

          h2 {
            display: none;
          }
        }
      `};
  }

  ${props => props.theme.media.mobile} {
    & > * + * {
      margin-top: 1rem;
    }
  }
`
const ContactAndCard = styled.div`
  grid-area: contact-and-card;
  display: flex;
  flex-direction: column;

  & > * + * {
    margin-top: 1.5rem;
  }

  ${props => props.theme.media.mobile} {
    & > * + * {
      margin-top: 1rem;
    }
  }
`

const SalaryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
  grid-area: salary-header;

  & > span {
    display: flex;
    gap: 1rem;
  }
`

const SalaryBody = styled.div`
  grid-area: salary;
  display: flex;
  flex-direction: column;
  gap: 2rem;
`

const Create = styled.div`
  position: relative;
`

const EditSalaryGroup = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;

  & > div {
    padding: 8px 12px 5px;
    border-radius: 6px;
    font-size: 1.2rem;
    color: white;
    background: ${props => props.theme.colors.secondary};

    i {
      margin-left: 1.4ch;

      color: rgba(0, 0, 0, 0.4);
      font-weight: 300;
      font-size: 0.8em;
      cursor: pointer;

      &:hover {
        color: white;
      }
    }
  }
  span.error {
    margin-left: 1ch;
    color: ${props => props.theme.colors.red};
    font-size: 1rem;
  }
`

const AddSalaryGroup = styled.div.attrs({
  role: 'button',
})`
  position: relative;
  padding: 6px 12px 7px !important;
  background: white !important;
  border: 1px solid ${props => props.theme.colors.gray8};
  cursor: pointer;

  i {
    margin-left: 0 !important;
  }
  &:hover {
    background: ${props => props.theme.colors.secondary} !important;
    border-color: rgba(255, 255, 255, 0);

    i {
      color: white;
    }
  }
  aside {
    position: absolute;
    z-index: ${ZIndexRange.Dropdowns};
    top: -1px;
    left: -1px;

    background: white;
    border: 1px solid ${props => props.theme.colors.gray8};
    border-radius: 6px;
    overflow: hidden;

    & > div {
      display: flex;
      align-items: center;
      height: 36px;
      padding: 0 12px;

      color: ${props => props.theme.colors.text.dark};
      font-size: 0.8em;
      cursor: pointer;

      & + div {
        border-top: 1px solid ${props => props.theme.colors.gray8};
      }
      &:hover {
        background-color: ${props => props.theme.colors.quaternary};
      }
    }
  }
`

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;

  h2 {
    margin-bottom: 1rem;
  }
  p {
    margin: 0.5rem 0 0 0;
    font-weight: 600;
  }
`

export const UserProfile: React.FC = () => {
  const translations = useTranslate({
    salarySettings: 'settings.salary-and-work-time',
    loading: 'common.loading',
    myProfile: 'users.my-profile',
    profileOfUser: ['users.profile-of-user', { user: <span /> }],

    editProfile: 'users.edit-profile',
    cancel: 'common.cancel',
    saveChanges: 'common.save-changes',

    noChanges: 'common.no-changes',
    profileDidChange: 'users.profile-did-change',
    add: 'common.add',

    oldCards: 'users.old-cards',
    theEmail: 'common.the-email',

    leaderGroups: 'companies.leader-groups',
    salaryGroups: 'salary.salary-groups',
    none: 'common.none',

    error: {
      phoneNumberTaken: 'users.errors.phone-number-taken',
      couldNotChange: 'error.could-not-change-profile-information',
      phoneNumberInvalid: 'error.invalid-phone-number',
      adressrequired: 'error.address-required',
      contactPersonRequired: 'error.contact-person-required',
      validEmailRequired: 'error.valid-email-required',
      needAtleastOneUserType: 'error.need-at-least-one-user-type',
      invalidPhoneNumber: 'error.invalid-phone-number',
      duplicate: {
        typeExist: ['error.type-already-exist', { type: '' }],
      },
    },
    confirm: {
      numberExists: ['users.number-already-exists', { name: '' }],
      deleteExistingNumber: 'users.delete-existing-number',
    },
  })

  const userId = useParam('userId')

  const location = useLocation()
  const navigate = useNavigate()

  const salaryCodesMenuRef = useRef<HTMLElement>(null)

  const company = useCompany()
  const admin = useAdmin()
  const me = useUser()
  const { setOverride, setOverrides, deleteOverrides } = useBreadcrumbs()
  const { hasPermissions, hasPermissionsOrMe } = usePermissions()
  const myProfile = me.id === userId
  const canSeeUserDocuments = hasPermissions(
    PERMISSIONS.users.view.userdocument
  )
  const canSeeUserEquipment = hasPermissions(
    PERMISSIONS.users.view.userequipment
  )
  const addToast = useToast()
  const onErrorAuto = useOnErrorAuto()
  const confirm = useConfirm()

  const [salaryCodesOpen, setSalaryCodesOpen] = useState(false)
  const [editing, setEditing] = useState(!!location.state?.edit)
  const [initialLoad, setInitialLoad] = useState(false)
  const [selectSalaryModuleOpen, setSelectSalaryModuleOpen] = useState(false)
  const [originalUserTypes, setOriginalUserTypes] = useState<string[]>([])

  const validateEmail = (value: string) =>
    value.match(emailIsValid) ? null : translations.error.validEmailRequired
  const validateUserTypes = (ut: Pick<UserTypeNode, 'name' | 'id'>[]) =>
    ut.length > 0 ? null : translations.error.needAtleastOneUserType
  const validatePhone = (s: string) =>
    isPossibleNumber(s) ? null : translations.error.invalidPhoneNumber

  const validateString = (translation: string) => (s: string) =>
    notBlankValidation(s) ? null : translation

  const {
    formValues: editForm,
    formEdited,
    formValid,
    formErrors: errors,
    updateForm,
    updateForm: updateEditForm,
    updateInitialValues,
    submitHandler,
    resetForm,
    updateFormErrors,
  } = useForm<ProfileEditForm>({
    values: {
      phoneNumber: '',
      email: '',
      address: '',
      city: '',
      postalCode: '',
      userTypes: [],
      contactPersonName: '',
      contactPersonPhoneNumber: '',
      profilePicture: null,
      internalNumber: '',
      birthNumber: '',
      salaryGroup: null,
      startedAt: null,
    },
    validators: {
      email: validateEmail,
      address: validateString(translations.error.adressrequired),
      contactPersonName: validateString(
        translations.error.contactPersonRequired
      ),
      userTypes: validateUserTypes,
      phoneNumber: validatePhone,
      contactPersonPhoneNumber: validatePhone,
    },
  })

  const { data: profileData, loading: profileLoading } = useQuery(
    PROFILE_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      variables: { id: userId, withDocuments: false },
      onCompleted(data) {
        if (!data || formEdited) return
        const { user } = data

        const values: ProfileEditForm = {
          phoneNumber: user?.phoneNumber ?? '',
          email: user.email,
          address: user.address,
          city: user.city,
          postalCode: user.postalCode,
          userTypes: user.userTypes,
          contactPersonName: user.contactPersonName,
          contactPersonPhoneNumber: user.contactPersonPhoneNumber,
          profilePicture: user.profilePicture,
          internalNumber: user.internalNumber,
          birthNumber: user.birthNumber,
          salaryGroup: user.salaryGroups?.[0]?.id ?? '',
          startedAt: user.startedAt ? new Date(user.startedAt) : null,
        }

        setOriginalUserTypes(user.userTypes.map(userType => userType.id))

        updateForm(values)
        updateInitialValues(values)

        setInitialLoad(true)
      },
      onError: onErrorAuto(),
    }
  )

  const canEdit = hasPermissionsOrMe(
    profileData?.user ?? null,
    PERMISSIONS.users.change.user
  )

  const [patchUserMutation, { loading: patchLoading }] = useMutation(
    PATCH_USER_MUTATION,
    {
      refetchQueries: ['Profile'],
      onCompleted({ patchUserMutation: { user: userReturn } }) {
        if (!userReturn) return

        if (userReturn.id !== userId && userReturn.phoneNumber !== '') {
          void handlePhoneNumberTaken(userReturn)
        } else if (userReturn.phoneNumber !== '') {
          addToast('success', translations.profileDidChange)
          setEditing(false)
        } else if (!myProfile && admin) {
          addToast('success', translations.profileDidChange)
          setEditing(false)
        }
      },
      onError: onErrorAuto(
        {},
        {
          showToast: false,
          callback({ message }: { message: string }) {
            if (
              message.includes('A user with this email/username already exists')
            ) {
              addToast(
                'warning',
                translations.error.duplicate.typeExist({
                  type: translations.theEmail,
                })
              )
              updateFormErrors({
                email: translations.error.duplicate.typeExist({
                  type: translations.theEmail,
                }),
              })
              return
            }
            resetForm()
          },
        }
      ),
    }
  )

  async function handlePhoneNumberTaken(
    userWithExistingNumber: Pick<UserNode, 'id' | 'fullName'>
  ) {
    const { data: answer } = await confirm(
      translations.confirm.deleteExistingNumber,
      translations.confirm.numberExists({
        name: userWithExistingNumber.fullName,
      })
    )
    if (!answer) return

    try {
      await patchUserMutation({
        variables: {
          id: userWithExistingNumber.id,
          input: {
            phoneNumber: '',
          },
        },
      })
    } finally {
      handleSubmit(editForm)
    }
  }

  function handleCancel() {
    resetForm()
    setEditing(false)
  }

  function handleSubmit(values: ProfileEditForm) {
    if (!profileData?.user) return

    if (!formEdited) {
      addToast('info', translations.noChanges)
      setEditing(false)
      return
    }

    const { userTypes, salaryGroup, startedAt, ...rest } = values

    const userTypeIds = userTypes.map(userType => userType.id)
    const userTypesRemove = difference(originalUserTypes, userTypeIds)
    const userTypesAdd = userTypeIds.reduce<string[]>((acc, cur) => {
      if (userTypesRemove.includes(cur) || originalUserTypes.includes(cur))
        return acc
      return [...acc, cur]
    }, [])

    void patchUserMutation({
      variables: {
        id: profileData?.user.id,
        input: {
          isActive: true,
          startedAt: !startedAt ? null : startedAt.toISOString().slice(0, 10),
          ...rest,
          profilePicture:
            rest.profilePicture instanceof File
              ? rest.profilePicture
              : undefined,
          userTypesAdd,
          userTypesRemove,
        },
      },
    })
  }

  // TODO fix exhaustive-deps?
  // eslint-disable-next-line
  const setOverrideCallback = useCallback(setOverride, [])
  // eslint-disable-next-line
  const setOverridesCallback = useCallback(setOverrides, [])
  // eslint-disable-next-line
  const deleteOverridesCallback = useCallback(deleteOverrides, [])
  useEffect(() => {
    if (myProfile)
      setOverridesCallback({
        users: null,
        [userId]: translations.myProfile,
      })
    else if (!profileData?.user)
      setOverrideCallback(userId, translations.loading)
    else setOverrideCallback(profileData.user.id, profileData.user.fullName)

    return () => deleteOverridesCallback(userId, 'users')
  }, [
    profileData,
    myProfile,
    setOverrideCallback,
    setOverridesCallback,
    deleteOverridesCallback,
    translations.loading,
    translations.myProfile,
    userId,
  ])

  const canSeeAddonOverview = hasPermissions(
    PERMISSIONS.salary.view.timeaddon,
    false
  )

  const user = profileData?.user ?? null

  const leaderGroupNames = profileLoading
    ? ''
    : user?.leaderGroupsAsUser.length
    ? user?.leaderGroupsAsUser
        .map(leaderGroup => leaderGroup.name)
        .sort()
        .join(', ')
    : translations.none

  const salaryGroupNames = profileLoading
    ? ''
    : user?.salaryGroups.length
    ? user?.salaryGroups
        .map(salaryGroup => salaryGroup.name)
        .sort()
        .join(', ')
    : translations.none

  const getModuleTranslation = useSalaryModuleTranslations()

  const oldCards =
    user?.driverCards.filter(
      driverCard => driverCard.id !== user?.activeDriverCard?.id
    ) ?? []

  const cancelButtonContent = (() => {
    if (!editing) return null
    if (isMobile) return <Icon icon="times" />
    return translations.cancel
  })()

  const editButtonContent = (() => {
    if (isMobile) {
      if (editing) return <Icon icon="check" />
      else return <Icon icon="edit" />
    } else {
      if (editing) return translations.saveChanges
      else return translations.editProfile
    }
  })()

  useClickOutside(salaryCodesMenuRef, () => setSalaryCodesOpen(false), true)

  return (
    <Wrapper $editing={editing}>
      <header>
        <TitleWrapper>
          <h2>
            {myProfile ? (
              <span>{translations.myProfile}</span>
            ) : (
              <>
                {translations.profileOfUser({
                  user: <span>{user?.fullName ?? translations.loading}</span>,
                })}
              </>
            )}
          </h2>

          <p>
            {!editing && `${translations.leaderGroups}: ${leaderGroupNames}`}
          </p>

          <p>
            {!editing && `${translations.salaryGroups}: ${salaryGroupNames}`}
          </p>
        </TitleWrapper>

        {canEdit && (
          <div>
            {cancelButtonContent && (
              <Button
                variant="outline"
                size={isMobile ? 'icon' : 'default'}
                onClick={handleCancel}
              >
                {cancelButtonContent}
              </Button>
            )}

            <Button
              variant={editing ? 'default' : 'outline'}
              size={isMobile ? 'icon' : 'default'}
              disabled={
                editing && !(formEdited && formValid) && (myProfile || !admin)
              }
              onClick={
                editing
                  ? !myProfile && admin
                    ? () => handleSubmit(editForm)
                    : submitHandler(handleSubmit)
                  : () => setEditing(v => !v)
              }
            >
              {editButtonContent}
            </Button>
          </div>
        )}
      </header>

      <MainInfo
        user={user}
        loading={profileLoading || patchLoading}
        editing={editing}
        initialLoad={initialLoad}
        editForm={editForm}
        errors={errors}
        onUpdateForm={updateEditForm}
      />

      <ContactAndCard>
        <ContactPerson
          user={user}
          editing={editing}
          editForm={editForm}
          errors={errors}
          onUpdateForm={updateEditForm}
        />

        {user &&
          canEdit &&
          (!user.activeDriverCard ? (
            <AddDriverCard user={user} />
          ) : (
            <DriverCard
              area="driverCard"
              cardId={user.activeDriverCard.id}
              active
            />
          ))}
      </ContactAndCard>

      {(canSeeUserDocuments || admin) && <UserDocuments userId={userId} />}

      {(canSeeUserEquipment || admin) && <UserEquipmentTable userId={userId} />}

      {canSeeAddonOverview && (
        <div className="space-y-4">
          <FixedHourlyAddons type="user" typeId={userId} />
          <FixedSalaryDayAddons type="user" typeId={userId} />
          <FixedSalaryAddons type="user" typeId={userId} />
        </div>
      )}

      {admin && (
        <>
          {oldCards.length > 0 && (
            <ExpiredCards>
              <h2>{translations.oldCards}</h2>
              {oldCards.map(driverCard => (
                <DriverCard
                  area="expired-cards"
                  cardId={driverCard.id}
                  active={false}
                  key={driverCard.id}
                />
              ))}
            </ExpiredCards>
          )}

          <SalaryHeader>
            <span>
              <h2>{translations.salarySettings}</h2>

              <EditSalaryGroup>
                {editForm.salaryGroup && !profileLoading && (
                  <div>
                    {
                      company.salaryGroups.find(
                        group => group.id === editForm.salaryGroup
                      )?.name
                    }
                    <Icon
                      icon="times"
                      onClick={() => {
                        updateForm({
                          salaryGroup: null,
                        })
                        void patchUserMutation({
                          variables: {
                            id: userId,
                            input: { salaryGroups: [] },
                          },
                        })
                      }}
                    />
                  </div>
                )}

                {!editForm.salaryGroup && (
                  <AddSalaryGroup onClick={() => setSalaryCodesOpen(true)}>
                    {profileLoading ? (
                      <Loader.Spinner size={18} thickness={2} />
                    ) : (
                      <Icon icon="plus" />
                    )}

                    {salaryCodesOpen && (
                      <aside
                        ref={salaryCodesMenuRef}
                        onClick={evt => evt.stopPropagation()}
                      >
                        {company.salaryGroups.map(group => (
                          <div
                            key={group.id}
                            role="button"
                            onClick={() => {
                              void patchUserMutation({
                                variables: {
                                  id: userId,
                                  input: { salaryGroups: [group.id] },
                                },
                              })
                              setSalaryCodesOpen(false)
                              updateForm({
                                salaryGroup: group.id,
                              })
                            }}
                          >
                            {group.name}
                          </div>
                        ))}
                      </aside>
                    )}
                  </AddSalaryGroup>
                )}
              </EditSalaryGroup>
            </span>

            {(user?.salaryWorkTimes.length ?? 0) < 3 && (
              <Create>
                {isMobileOnly ? (
                  <OldButton
                    height="32px"
                    padding="0 1rem"
                    onClick={() => setSelectSalaryModuleOpen(true)}
                  >
                    <FAIcon icon="plus" $size="0.8rem" />
                  </OldButton>
                ) : (
                  <OldButton
                    height="48px"
                    onClick={() => setSelectSalaryModuleOpen(true)}
                  >
                    {translations.add}
                  </OldButton>
                )}

                <SelectSalaryModule
                  open={selectSalaryModuleOpen}
                  exclude={
                    user?.salaryWorkTimes.reduce<SalaryModuleType[]>(
                      (acc, cur) => {
                        if (!cur.moduleConfig) return acc
                        return [...acc, cur.moduleConfig.name]
                      },
                      []
                    ) ?? []
                  }
                  onClose={() => setSelectSalaryModuleOpen(false)}
                  onSubmit={salaryModule =>
                    void navigate(`/users/${userId}/salary/create`, {
                      state: { salaryModule },
                    })
                  }
                />
              </Create>
            )}
          </SalaryHeader>

          <SalaryBody>
            {user?.salaryWorkTimes.map(salaryWorkTime => {
              if (!salaryWorkTime.moduleConfig) return null

              const icon =
                salaryWorkTime.moduleConfig.name === 'Activities'
                  ? 'truck'
                  : salaryWorkTime.moduleConfig.name === 'Terminal'
                  ? 'house'
                  : 'clock'
              const iconWidth =
                salaryWorkTime.moduleConfig.name === 'Timesheets'
                  ? '20px'
                  : '28px'

              return (
                <OldProfileCard key={salaryWorkTime.id}>
                  <header>
                    <SVGIcon icon={icon} fill="black" width={iconWidth} />
                    <h3>
                      {getModuleTranslation(salaryWorkTime.moduleConfig.name)}
                    </h3>
                  </header>

                  <SalaryWorkTimesTables salaryWorkTimes={[salaryWorkTime]} />
                </OldProfileCard>
              )
            })}
          </SalaryBody>
        </>
      )}

      <HideInProduction>
        <ChildcareCard />
      </HideInProduction>
    </Wrapper>
  )
}
